<script lang="ts" setup>
import ChevronRight from '~/assets/icons/arrows/16/chevron-right.svg'
import ChevronLeft from '~/assets/icons/arrows/16/chevron-left.svg'
import type { IPagination } from '~/shared/interfaces/ui/pagination'

const props = withDefaults(defineProps<IPagination>(), {
  paginationSettings: () => {
    return {
      paginationType: undefined,
      closingNumsCount: 5,
      countBetweenEllipsis: 1,
      countForShowArrows: 2,
      countForShowEllipsis: 7,
    }
  }
})

const emits = defineEmits<{
  (event: 'handle-pagination', page: number): void,
  (event: 'handle-watch-more', page: number): void
}>();

const getPaginationList = computed(() => {
  if (props.paginationSettings) {
    const { closingNumsCount, countBetweenEllipsis, countForShowEllipsis } = props.paginationSettings;
    const lastPage = props.lastPage;
    const currentPage = props.currentPage;
    const endClosingNum = lastPage - closingNumsCount;

    if (currentPage) {
      if (lastPage <= countForShowEllipsis) {
        return getRange(1, lastPage);
      }
      if (currentPage < closingNumsCount) {
        return [
          ...getRange(1, closingNumsCount),
          null,
          lastPage
        ]
      }
      if (currentPage > endClosingNum) {
        return [
          1,
          null,
          ...getRange(endClosingNum + 1, lastPage)
        ]
      }
      return [
        1,
        null,
        ...getRange(currentPage - countBetweenEllipsis, currentPage + countBetweenEllipsis),
        null,
        lastPage
      ]
    }
  }

  return null;
})

function getRange(start: number, stop: number) {
  return Array.from({ length: (stop - start) / 1 + 1 }, (_, i) => start + i * 1);
}

function handlePagination(page: number) {
  emits('handle-pagination', page);
  // Прокрутка в начало страницы
  window.scrollTo({ top: 0, behavior: 'smooth' });
}

function handleWatchMore(page: number) {
  emits('handle-watch-more', page);
}

</script>

<template>
  <div class="pagination">
    <button
      class="pagination__more"
      v-if="(!paginationType || paginationType == 'watch-more') && currentPage < lastPage"
      :disabled="currentPage == lastPage"
      @click="handleWatchMore(currentPage + 1)"
    >
      Показать ещё
    </button>
    <div
      class="pagination__container"
      v-if="(!paginationType || paginationType == 'pagination') && paginationSettings"
    >
      <div
        class="pagination-arrow"
        @click="handlePagination(currentPage - 1)"
        v-if="paginationSettings.countForShowArrows < lastPage && currentPage > 1"
      >
        <ChevronLeft/>
      </div>

      <div class="pagination-pages">
        <template v-for="page in getPaginationList">
          <div
            class="pagination-pages__page"
            :class="[
              currentPage == page && 'pagination-pages__page_active'
            ]"
            @click="handlePagination(page)"
            v-if="page"
          >
            {{ page }}
          </div>
          <div
            class="pagination-pages__ellipsis"
            v-else
          >
            ...
          </div>
        </template>
      </div>

      <div
        class="pagination-arrow"
        @click="handlePagination(currentPage + 1)"
        v-if="paginationSettings.countForShowArrows < lastPage && currentPage < lastPage"
      >
        <ChevronRight/>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.pagination {
  &__container {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 20px;
  }

  &-pages {
    display: flex;
    gap: 4px;

    &__ellipsis {
      cursor: default;
    }

    &__page, &__ellipsis {
      width: 40px;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 8px;
      cursor: pointer;
      @include font(14px, 20px, 500);
      transition: background-color .3s;

      &:hover:not(:active, .pagination-pages__page_active) {
        @include mf-breakpoint {
          background-color: $control-bg_neutral;
        }
      }

      &_active {
        background-color: $control-bg_primary;
        color: $control-content_white;
      }
    }
  }

  &-arrow {
    cursor: pointer;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    transition: background-color .3s;

    &:hover:not(:active) {
      @include mf-breakpoint {
        background-color: $control-bg_neutral;
      }
    }
  }
  &__more {
    @include font(16px, 20px, 500);
    width: 100%;
    padding: 13px 19px;
    border-radius: 8px;
    border: 1px solid $control-border_grey;
    transition: box-shadow .2s;

    &:hover:not(:active) {
      @include mf-breakpoint {
        box-shadow: inset 0 0 0 2px $control-border_black
      }
    }
  }
}
</style>
